import {
  PropertyRequest,
  GetPropertiesFilters,
} from "../../types/listings/listing.model";
import { api } from "../axios";
import { postLead, type LeadSource } from "../v2Leads";
import { isHiddenListing, normalizeListing } from "../../helpers/listingNormalize";
import { apiUrl, backendFeatures } from "../../config/backendFeatures";
import { cleanPage, getBishkekMerged, getChuiWithoutBishkek, promotedFirst, type Page } from "./legacyCompat";

const CREATE_PROPERTY_URL = import.meta.env.VITE_CREATE_PROPERTY_URL;
const GET_PROPERTIES_URL = import.meta.env.VITE_GET_PROPERTIES_URL;
const GET_PROPERTY_BY_ID = import.meta.env.VITE_GET_PROPERTY_BY_ID;
const GET_PROPERTIES_BY_OWNER_ID = import.meta.env.VITE_GET_PROPERTIES_BY_OWNER_ID
const GENERATE_PROPERTY_DESCRIPTION_URL = import.meta.env.VITE_GENERATE_PROPERTY_DESCRIPTION_URL;

/**
 * Query string for list filters. Values are URL-encoded (a search like
 * «2-комн. & лоджия» used to break the request), arrays become repeated keys
 * (`userType=REALTOR&userType=AGENCY`, what the API's DTO expects), and empty
 * values are left out — the server applies its own defaults.
 */
export const queryObjects = (object: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(object ?? {})) {
    if (Array.isArray(value)) {
      for (const v of value) if (v !== undefined && v !== null && v !== "") params.append(key, String(v));
    } else if (value !== undefined && value !== null && value !== "" && value !== false && value !== 0) {
      params.append(key, String(value));
    }
  }
  return params.toString();
};

export const createProperty = async (data: PropertyRequest) => {
  try {
    const res = await api.post(CREATE_PROPERTY_URL, data);
    return res.data;
  } catch (error) {
    console.log("createListing in listingApi error: ", error);
    throw error;
  }
};

const rawGetProperties = async (filters: Record<string, any>) => {
  const res = await api.get(GET_PROPERTIES_URL + queryObjects(filters));
  return res.data;
};

/**
 * The listing search. Filtering, search text, sorting (incl. HOT → VIP → TOP)
 * and pagination are the server's job — the browser sends the filters and
 * shows the page it gets. The only exception is the Bishkek/Chui split while
 * backend task B1 is pending (legacyCompat.ts).
 */
export const getProperties = async (filters: GetPropertiesFilters): Promise<Page> => {
  try {
    const f = filters as Record<string, any>;
    if (!backendFeatures.bishkekMigrated && !f.district) {
      if (f.region === "BISHKEK") return promotedFirst(await getBishkekMerged(rawGetProperties, f));
      if (f.region === "CHUI") return promotedFirst(await getChuiWithoutBishkek(rawGetProperties, f));
    }
    // The server already orders HOT → VIP → TOP → rest; nothing to re-sort here.
    return cleanPage(await rawGetProperties(f));
  } catch (error) {
    console.log("getProperties in listingApi error: ", error);
    throw error;
  }
};

/** Count only (no impressions counted): GET /api/property/count. */
export const countProperties = async (filters: Record<string, unknown>): Promise<number> => {
  const res = await api.get(apiUrl(`/api/property/count?${queryObjects(filters)}`));
  return Number(res.data?.total ?? 0);
};

/** Similar listings chosen by the server: GET /api/property/:id/similar — task B5. */
export const getSimilarProperties = async (id: number | string, limit = 8): Promise<Page> => {
  const res = await api.get(apiUrl(`/api/property/${encodeURIComponent(String(id))}/similar?limit=${limit}`));
  return cleanPage(Array.isArray(res.data) ? { list: res.data, total: res.data.length } : res.data);
};

export { isHiddenListing, normalizeListing };

/**
 * «Someone contacted the seller» (phone revealed, chat started) — the
 * server counts it in leads_total for the seller's analytics and ignores the
 * owner's own clicks. Once per listing per browser session; never blocks the UI.
 *
 * The same contact also becomes a CRM lead (POST /api/v2/leads) of the
 * listing's workspace — an independent realtor gets it in «Лиды», a team
 * realtor gets it inside the agency. Before this only profile chats made a
 * lead, so a realtor whose clients wrote from a listing saw an empty list.
 */
export function registerLead(propertyId: number | string | undefined | null, source?: LeadSource): void {
  if (propertyId === undefined || propertyId === null || propertyId === "") return;
  const id = Number(propertyId);
  if (source && Number.isFinite(id) && id > 0) void postLead({ source, property_id: id });
  const key = `lead:${propertyId}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    /* private mode — count anyway */
  }
  void api.post(apiUrl(`/api/property/${encodeURIComponent(String(propertyId))}/lead`)).catch(() => undefined);
}

/** B12 — light map markers for the whole country (or a box); views are not counted. */
export const getMapMarkers = async (params: Record<string, string | number> = {}) => {
  const res = await api.get(apiUrl("/api/property/map"), { params: { limit: 1000, ...params } });
  const list = Array.isArray(res.data?.list) ? res.data.list.map(normalizeListing) : [];
  return { list, total: Number(res.data?.total ?? list.length), truncated: Boolean(res.data?.truncated) };
};

export const getPropertyById = async (propertyId: string) => {
  try {
    const propertyById = await api.get(GET_PROPERTY_BY_ID + propertyId);
    return normalizeListing(propertyById.data);
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getPropertiesByOwnwerId = async (ownerId: number) => {
  try {
    const res = await api.get(GET_PROPERTIES_BY_OWNER_ID + ownerId);
    const data = res.data;
    // Owner lists come back as a bare array or as a page — clean either shape.
    if (Array.isArray(data)) return data.filter((p) => !isHiddenListing(p)).map(normalizeListing);
    return cleanPage(data);
  } catch (error) {
    console.log(error);
    throw error;
  }
}

// AI features. With task B7 deployed the browser only sends data — the
// prompt, model and limits live on the server, so the AI endpoint can't be
// used as a free general-purpose chatbot. Until then the legacy path below
// builds the prompt here and posts it to the generic /deepseek/chat endpoint.

export const generateDescriptionApi = async (infoForDescription: string) => {
  if (backendFeatures.ai) {
    const res = await api.post(apiUrl("/api/property/description/generate"), { facts: infoForDescription });
    return String(res.data?.description ?? "");
  }
  try {
    const res = await api.post(`${GENERATE_PROPERTY_DESCRIPTION_URL}`, {
      message: JSON.stringify(infoForDescription)/* + ' Here are images urls: ' + images.join(', ')*/,
      model: "deepseek-chat",
      thinking: true,
      reasoningEffort: "low",
    });
    return res.data?.content || "";
  } catch (error) {
    console.log("generateDescriptionApi in listingApi error: ", error);
    throw error;
  }
}

/** UI language code or language name → the ISO code /api/ai/translate takes. */
const ISO_LANG: Record<string, string> = {
  ru: "ru", russian: "ru",
  kg: "ky", ky: "ky", kyrgyz: "ky",
  kz: "kk", kk: "kk", kazakh: "kk",
  uz: "uz", uzbek: "uz",
  tr: "tr", turkish: "tr",
  en: "en", english: "en",
  zh: "zh", chinese: "zh",
};
export const toIsoLang = (lang: string) => ISO_LANG[lang.trim().split(/\s+/)[0].toLowerCase()] ?? "ru";

export const translateTextApi = async (text: string, targetLang: string) => {
  if (backendFeatures.ai) {
    const res = await api.post(apiUrl("/api/ai/translate"), { text, targetLang: toIsoLang(targetLang) });
    return String(res.data?.text ?? "");
  }
  try {
    const res = await api.post(`${GENERATE_PROPERTY_DESCRIPTION_URL}`, {
      message: `
Translate the following real estate listing description into ${targetLang}.

Return ONLY the translated text, with no commentary, notes, quotation marks, or metadata.
Preserve the original formatting (line breaks, emojis, bullet points).
Do not translate numbers, addresses written in Latin script, or ID codes — keep them as-is.

Text to translate:
"""
${text}
"""
      `,
      model: "deepseek-chat",
      thinking: false,
      reasoningEffort: "low",
    });
    return res.data?.content || "";
  } catch (error) {
    console.log("translateTextApi error: ", error);
    throw error;
  }
};

/** B17: a chat message by id — the server reads the text and checks I may see it. */
export const translateChatMessageApi = async (messageId: string, targetLang: string) => {
  const res = await api.post(apiUrl("/api/ai/translate"), { messageId, targetLang: toIsoLang(targetLang) });
  return String(res.data?.text ?? "");
};